import { MenuId, SectionId, ItemId } from './enum';
import { menuSlugs, sectionSlugs, itemSlugs, LOCALES, Locale } from './localized-paths';

// Build translation maps for each locale
function buildPathTranslations() {
  const translations: Record<string, Record<string, string>> = {};
  for (const fromLocale of LOCALES) {
    const toLocale = LOCALES.find(l => l !== fromLocale)!;
    const map: Record<string, string> = {};
    for (const menu of Object.values(MenuId)) {
      const fromMenu = `/${menuSlugs[fromLocale][menu]}`;
      const toMenu = `/${menuSlugs[toLocale][menu]}`;
      map[fromMenu] = toMenu;
    }
    for (const section of Object.values(SectionId)) {
      const fromSection = `/${sectionSlugs[fromLocale][section]}`;
      const toSection = `/${sectionSlugs[toLocale][section]}`;
      map[fromSection] = toSection;
    }
    for (const item of Object.values(ItemId)) {
      const fromItem = `/${itemSlugs[fromLocale][item]}`;
      const toItem = `/${itemSlugs[toLocale][item]}`;
      map[fromItem] = toItem;
    }
    // Compose menu/section/item paths
    for (const menu of Object.values(MenuId)) {
      for (const section of Object.values(SectionId)) {
        const fromMenuSection = `/${menuSlugs[fromLocale][menu]}/${sectionSlugs[fromLocale][section]}`;
        const toMenuSection = `/${menuSlugs[toLocale][menu]}/${sectionSlugs[toLocale][section]}`;
        map[fromMenuSection] = toMenuSection;
        for (const item of Object.values(ItemId)) {
          const fromFull = `/${menuSlugs[fromLocale][menu]}/${sectionSlugs[fromLocale][section]}/${itemSlugs[fromLocale][item]}`;
          const toFull = `/${menuSlugs[toLocale][menu]}/${sectionSlugs[toLocale][section]}/${itemSlugs[toLocale][item]}`;
          map[fromFull] = toFull;
        }
      }
      // Compose menu/item paths (2 segments). Some navigation trees place items
      // directly under a menu, e.g. /informasi-pengadaan/daftar-perencanaan.
      for (const item of Object.values(ItemId)) {
        const fromMenuItem = `/${menuSlugs[fromLocale][menu]}/${itemSlugs[fromLocale][item]}`;
        const toMenuItem = `/${menuSlugs[toLocale][menu]}/${itemSlugs[toLocale][item]}`;
        map[fromMenuItem] = toMenuItem;
      }
    }
    translations[fromLocale] = map;
  }

  // Procurement information is a standalone top-level menu in the route tree,
  // even though its child pages use the same procurement-information section
  // slug. The generic menu/section/item composition above treats that first
  // segment as Publications and therefore misses these actual page paths.
  for (const [fromLocale, toLocale] of [['id', 'en'], ['en', 'id']] as const) {
    const fromMenu = menuSlugs[fromLocale][MenuId.ProcurementInformation];
    const toMenu = menuSlugs[toLocale][MenuId.ProcurementInformation];
    const fromSection = sectionSlugs[fromLocale][SectionId.ProcurementInformation];
    const toSection = sectionSlugs[toLocale][SectionId.ProcurementInformation];

    for (const item of [ItemId.ListOfPlannedProcurement, ItemId.TenderInformation]) {
      const fromPath = `/${fromMenu}/${fromSection}/${itemSlugs[fromLocale][item]}`;
      const toPath = `/${toMenu}/${toSection}/${itemSlugs[toLocale][item]}`;
      mapPath(translations[fromLocale], fromPath, toPath);
    }
  }

  // Explicit overrides for paths where section slug in URL differs from sectionSlugs definition.
  // next.config.ts rewrites use 'training' (EN) as section segment in the ID locale URL.
  const trainingItems: Array<[string, string]> = [
    ['pelatihan-jadwal', 'training-schedule'],
    ['sekilas', 'training-overview'],
  ];
  for (const [idItem, enItem] of trainingItems) {
    translations['id'][`/layanan/training/${idItem}`] = `/services/training/${enItem}`;
    translations['en'][`/services/training/${enItem}`] = `/layanan/training/${idItem}`;
  }

  return translations;
}

function mapPath(map: Record<string, string>, fromPath: string, toPath: string) {
  map[fromPath] = toPath;
}

export const PATH_TRANSLATIONS = buildPathTranslations();
